/**
 * SurfaceGradient — THE NORMAL CONTRACT's one vocabulary (shader gaps program round 4, material models, 2026-09-30).
 *
 * Morten S. Mikkelsen, "Surface Gradient Based Bump Mapping Framework", JCGT 9(3), 2020
 * (https://jcgt.org/published/0009/03/04/) — every bump source (a tangent-space normal map, a procedural detail, a
 * volume function, a screen-space height, a decal) is converted to a SURFACE GRADIENT (a vector in the tangent plane
 * of one BASE normal, in the SAME space as that normal); gradients ADD (a weighted sum is a blend, a scale is a
 * strength, a negation inverts the bump); ONE resolve makes the shading normal:
 *
 *     n' = normalize(n − Σ sgᵢ)                                        (Listing 1)
 *
 * WHY here. EngineX and Atlas composed normals ad hoc — the detail surface, rain ripples, the ground's layers, the
 * rock bump, the damage crater and POM each perturbed in its own frame (the 2026-09-22 detail bug — a tangent-space
 * vector used as a view normal — was the symptom). This module is the shared, PINNED form of each operation, with a
 * CPU mirror per function so the harnesses can prove a routed composer is the SAME arithmetic it was before.
 *
 * THE FORMS (each exact against the legacy code it replaces — `tests/unit/material-models.test.ts` pins them):
 *   • `tspaceNormalToDerivative` (Listing 2) + `surfgradFromTBN` (Listing 3): a tangent-space normal m on a frame
 *     (T, B): d = −m.xy / |m.z|, sg = d.x·T + d.y·B. With m.z = 1 the resolve is normalize(n + m.x·T + m.y·B) — the
 *     detail surface's old formula, bit for bit (a division by 1 and two negations are exact in IEEE).
 *   • `surfgradFromVolumeGradient` (Listing 8): a 3D bump's gradient projected into the tangent plane.
 *   • `offsetAsSurfaceGradient`: the UNPROJECTED form the legacy composers use — they ADD a vector v to the normal
 *     (normalize(n + v)), which is resolve(n, −v). The contract names it and records the deviation from the projected
 *     form: normalize(n + v) tilts by |v⊥| / (1 + n·v) instead of |v⊥| — identical for a v in the tangent plane (a
 *     horizontal ripple on flat ground), a relative O(n·v) difference otherwise. The routes keep it (the look is pinned),
 *     new code uses the projected forms.
 *   • `surfgradScreenHeight` (Listing 6, isDerivScreenSpace): a height differentiated in screen space; with the
 *     rock bump's 0.15 m per unit height the resolve is Mikkelsen 2010's normalize(|det|·n − s·grad) exactly (the
 *     same direction: scaled by 1/|det|).
 *   • `surfgradFromPerturbedNormal` (Listing 5): an already-perturbed normal back into a gradient (to blend it).
 *
 * Zero strength is the identity: resolve(n, 0) = normalize(n) = n for a unit n — every route multiplies its gradient
 * by its strength, so a strength of 0 returns the base normal (the routed composers were already written that way).
 *
 * VARIANCE (specular AA) is NOT carried by a gradient: the Toksvig term reads the RAW mip-filtered normal-map texel's
 * length (`SpecularAA`), the ground reads the length of its height-weighted mean layer normal, the detail surface hands
 * its unresolved slope energy to roughness — none of those reads a resolved contract normal, so routing changes none.
 *
 * TSL graphs are loosely typed (`any`), as elsewhere in `render/materials`.
 */
import { vec3, float, normalize, dot, cross, abs, max, sign } from 'three/tsl';

type N = any;
type V3 = [number, number, number];

// ─── TSL ─────────────────────────────────────────────────────────────────────

/** Listing 2: a tangent-space normal m ∈ [−1, 1]³ → the height derivative d = −(m.x, m.y) / max(|m.z|, |m.xy|max/128). */
export function tspaceNormalToDerivative(m: N): N {
  const ma: N = abs(m);
  const zma: N = max(ma.z, max(ma.x, ma.y).mul(1 / 128));
  return m.xy.negate().div(zma);
}

/** Listing 3: the TBN-style surface gradient d.x·T + d.y·B (T, B in the base normal's space). */
export function surfgradFromTBN(deriv: N, t: N, b: N): N {
  return t.mul(deriv.x).add(b.mul(deriv.y));
}

/** Listing 8: a volume bump's gradient (a 3D noise, a decal projector's field) projected into the base's tangent plane. */
export function surfgradFromVolumeGradient(n: N, grad: N): N {
  return grad.sub(n.mul(dot(n, grad)));
}

/** Listing 5: an already-perturbed normal v (any length, same space) back into a surface gradient. */
export function surfgradFromPerturbedNormal(n: N, v: N): N {
  const k: N = dot(n, v);
  return n.mul(k).sub(v).div(max(abs(k), float(1.192093e-7)));
}

/**
 * Listing 6 with screen-space derivatives: the surface gradient of a height H whose screen derivatives are (dHdx, dHdy),
 * on a surface whose position derivatives are (dPdx, dPdy), all in the base normal's space. `flip` (±1) is the
 * two-sided sign (three's `faceDirection`) folded into the determinant, as the rock bump always did.
 */
export function surfgradScreenHeight(n: N, dPdx: N, dPdy: N, dHdx: N, dHdy: N, flip: N = float(1)): N {
  const r1: N = cross(dPdy, n), r2: N = cross(n, dPdx);
  const det: N = dot(dPdx, r1).mul(flip);
  const s: N = sign(det).div(max(abs(det), float(1.192093e-15)));
  return r1.mul(dHdx).add(r2.mul(dHdy)).mul(s);
}

/**
 * The legacy OFFSET form: a composer that returns normalize(n + v) is resolving the (unprojected) gradient −v. Named so
 * every route reads as a contract call; new code should project (`surfgradFromVolumeGradient`).
 */
export function offsetAsSurfaceGradient(v: N): N {
  return v.negate();
}

/** Listing 1: THE resolve — normalize(n − sg). One per composer; strength 0 ⇒ the base normal. */
export function resolveSurfaceGradient(n: N, sg: N): N {
  return normalize(n.sub(sg));
}

/** A zero gradient (the identity), for accumulators. */
export function zeroSurfaceGradient(): N { return vec3(0, 0, 0); }

// ─── CPU mirrors (the tests and the Atlas harness pin the TSL above against these) ────────────────────────────────

const dot3 = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const sub3 = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add3 = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const mul3 = (a: V3, k: number): V3 => [a[0] * k, a[1] * k, a[2] * k];
const cross3 = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
export function normalize3(a: V3): V3 { const l = Math.hypot(a[0], a[1], a[2]); return l > 0 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0]; }

export function tspaceNormalToDerivativeCpu(m: V3): [number, number] {
  const zma = Math.max(Math.abs(m[2]), Math.max(Math.abs(m[0]), Math.abs(m[1])) / 128);
  return [-m[0] / zma, -m[1] / zma];
}
export function surfgradFromTBNCpu(d: [number, number], t: V3, b: V3): V3 { return add3(mul3(t, d[0]), mul3(b, d[1])); }
export function surfgradFromVolumeGradientCpu(n: V3, g: V3): V3 { return sub3(g, mul3(n, dot3(n, g))); }
export function surfgradFromPerturbedNormalCpu(n: V3, v: V3): V3 {
  const k = dot3(n, v);
  return mul3(sub3(mul3(n, k), v), 1 / Math.max(Math.abs(k), 1.192093e-7));
}
export function surfgradScreenHeightCpu(n: V3, dPdx: V3, dPdy: V3, dHdx: number, dHdy: number, flip = 1): V3 {
  const r1 = cross3(dPdy, n), r2 = cross3(n, dPdx);
  const det = dot3(dPdx, r1) * flip;
  const s = Math.sign(det) / Math.max(Math.abs(det), 1.192093e-15);
  return mul3(add3(mul3(r1, dHdx), mul3(r2, dHdy)), s);
}
export function resolveSurfaceGradientCpu(n: V3, sg: V3): V3 { return normalize3(sub3(n, sg)); }
/** The angle (rad) between two directions — the harnesses' identity measure. */
export function angleBetweenCpu(a: V3, b: V3): number {
  const c = dot3(normalize3(a), normalize3(b));
  return Math.acos(Math.min(1, Math.max(-1, c)));
}
